/*
 *
 * Messaging
 *
 */

import React, { useState, useEffect } from "react"
import { connect } from "react-redux"
import { Helmet } from "react-helmet"
import { Input, Button, Spin, Tooltip } from "antd"
import {
	MessageOutlined,
	PlusOutlined,
	SearchOutlined,
	InboxOutlined,
} from "@ant-design/icons"
import { Group } from "firebase/groupsService"
import { useGroups } from "./hooks/useGroups"
import strings from "utils/strings"
import { useMessages } from "./hooks/useMessages"
import { useChatInput } from "./hooks/useChatInput"
import { useTyping } from "./hooks/useTyping"
import { useBotReply } from "./hooks/useBotReply"
import { GroupItem } from "./components/GroupItem"
import { ChatPanel } from "./components/ChatPanel"
import { CreateGroupModal } from "./components/CreateGroupModal"
import { EditGroupModal } from "./components/EditGroupModal"
import "./styles.scss"

interface MessagingProps {
	dispatch: any
	user: any
}

const Messaging: React.FC<MessagingProps> = ({ dispatch, user }) => {
	const loggedUserId: string = user?.user_id || ""
	const myName: string = user?.name || "Backoffice"

	const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null)
	const [showCreateModal, setShowCreateModal] = useState(false)
	const [showEditModal, setShowEditModal] = useState(false)
	const [pendingGroupId, setPendingGroupId] = useState<string | null>(null)

	const {
		groups,
		archivedGroups,
		loadingGroups,
		groupSearch,
		setGroupSearch,
		filteredGroups,
		filteredArchived,
		showArchived,
		setShowArchived,
	} = useGroups(dispatch)

	// selectedGroup searches both active and archived so archived chats remain viewable
	const selectedGroup =
		groups.find((g) => g.id === selectedGroupId) ??
		archivedGroups.find((g) => g.id === selectedGroupId) ??
		null

	const isSelectedArchived =
		!!selectedGroupId && archivedGroups.some((g) => g.id === selectedGroupId)

	const {
		messages,
		loadingMessages,
		showScrollBtn,
		floatingDate,
		chatMessagesRef,
		messagesEndRef,
		scrollToBottom,
		handleChatScroll,
		resetScroll,
	} = useMessages(selectedGroup, loggedUserId)

	const { typingNames, startTyping, stopTyping } = useTyping(
		selectedGroupId,
		loggedUserId,
		myName
	)

	const { triggerBotReply } = useBotReply(loggedUserId)

	const {
		inputText,
		sending,
		uploading,
		mentionQuery,
		mentionIndex,
		textareaRef,
		mentionPickerRef,
		imageInputRef,
		fileInputRef,
		handleSend,
		handleInputChange,
		handleKeyDown,
		handleImageClick,
		handleImageFileChange,
		handleFileClick,
		handleFileChange,
		insertMention,
		resetInput,
	} = useChatInput(
		selectedGroup,
		loggedUserId,
		myName,
		(isTyping) => (isTyping ? startTyping() : stopTyping()),
		(text) =>
			selectedGroup &&
			text.toLowerCase().includes("@agentd") &&
			triggerBotReply(selectedGroup, text)
	)

	// Auto-select a group once the realtime stream delivers it after creation
	useEffect(() => {
		if (!pendingGroupId) return
		const found = groups.find((g) => g.id === pendingGroupId)
		if (found) {
			setSelectedGroupId(found.id)
			setPendingGroupId(null)
		}
	}, [groups, pendingGroupId])

	const handleSelectGroup = (group: Group) => {
		setSelectedGroupId(group.id)
		resetInput()
		resetScroll()
	}

	const mentionMembers = selectedGroup
		? selectedGroup.members
				.filter((m) => m.id !== loggedUserId)
				.filter((m) => m.type !== "bot" || selectedGroup.hasBotActive)
				.filter(
					(m) =>
						mentionQuery !== null &&
						m.name.toLowerCase().includes(mentionQuery.toLowerCase())
				)
		: []

	return (
		<div className="MessagingWrapper">
			<Helmet>
				<title>{strings.messaging.pageTitle}</title>
			</Helmet>

			{/* Left panel */}
			<div className="groups-panel">
				<div className="groups-panel__header">
					<span className="groups-panel__title">
						<span className="title-icon">
							<MessageOutlined />
						</span>
						{strings.messaging.title}
					</span>
					<Tooltip title={strings.messaging.createGroup}>
						<Button
							size="small"
							icon={<PlusOutlined />}
							onClick={() => setShowCreateModal(true)}
							className="create-group-btn"
						/>
					</Tooltip>
				</div>

				<div className="groups-panel__search">
					<Input
						prefix={
							<SearchOutlined style={{ color: "rgba(255,255,255,0.45)" }} />
						}
						placeholder={strings.messaging.searchGroups}
						value={groupSearch}
						onChange={(e) => setGroupSearch(e.target.value)}
						className="group-search-input"
					/>
				</div>

				<div className="groups-panel__list">
					{loadingGroups ? (
						<div className="groups-panel__loading">
							<Spin />
						</div>
					) : filteredGroups.length === 0 && !showArchived ? (
						<div className="groups-panel__empty">
							{groupSearch
								? strings.messaging.noSearchResults
								: strings.messaging.noGroups}
						</div>
					) : (
						<>
							{filteredGroups.map((g) => (
								<GroupItem
									key={g.id}
									group={g}
									loggedUserId={loggedUserId}
									isSelected={selectedGroup?.id === g.id}
									onSelect={handleSelectGroup}
								/>
							))}

							{archivedGroups.length > 0 && (
								<div
									className="archived-section-toggle"
									onClick={() => setShowArchived((v) => !v)}
								>
									<InboxOutlined style={{ fontSize: 12 }} />
									<span>
										{showArchived
											? strings.messaging.hideArchived
											: strings.messaging.showArchived}
									</span>
									<span className="archived-section-toggle__count">
										{archivedGroups.length}
									</span>
								</div>
							)}

							{showArchived &&
								filteredArchived.map((g) => (
									<GroupItem
										key={g.id}
										group={g}
										loggedUserId={loggedUserId}
										isSelected={selectedGroup?.id === g.id}
										isArchived
										onSelect={handleSelectGroup}
									/>
								))}
						</>
					)}
				</div>
			</div>

			<ChatPanel
				selectedGroup={selectedGroup}
				messages={messages}
				loadingMessages={loadingMessages}
				loggedUserId={loggedUserId}
				showScrollBtn={showScrollBtn}
				floatingDate={floatingDate}
				chatMessagesRef={chatMessagesRef}
				messagesEndRef={messagesEndRef}
				scrollToBottom={scrollToBottom}
				handleChatScroll={handleChatScroll}
				typingNames={typingNames}
				mentionQuery={mentionQuery}
				mentionIndex={mentionIndex}
				mentionPickerRef={mentionPickerRef}
				mentionMembers={mentionMembers}
				insertMention={insertMention}
				inputText={inputText}
				sending={sending}
				uploading={uploading}
				imageInputRef={imageInputRef}
				fileInputRef={fileInputRef}
				handleFileClick={handleFileClick}
				handleFileChange={handleFileChange}
				textareaRef={textareaRef}
				handleSend={handleSend}
				handleInputChange={handleInputChange}
				handleKeyDown={handleKeyDown}
				handleImageClick={handleImageClick}
				handleImageFileChange={handleImageFileChange}
				onEditGroup={() => setShowEditModal(true)}
				onCreateGroup={() => setShowCreateModal(true)}
				isArchived={isSelectedArchived}
				botTyping={selectedGroup?.botTyping ?? false}
			/>

			<EditGroupModal
				visible={showEditModal && !!selectedGroup}
				selectedGroup={selectedGroup!}
				loggedUserId={loggedUserId}
				onClose={() => setShowEditModal(false)}
				onArchived={() => setSelectedGroupId(null)}
			/>

			<CreateGroupModal
				visible={showCreateModal}
				loggedUserId={loggedUserId}
				myName={myName}
				onClose={() => setShowCreateModal(false)}
				onCreated={(newId) => setPendingGroupId(newId)}
			/>
		</div>
	)
}

const mapStateToProps = (state: any) => ({
	user: state.user,
})

export default connect(mapStateToProps)(Messaging)
